import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { Tables } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import {
  ArrowLeft,
  Loader2,
  Save,
  Eye,
  EyeOff,
  ExternalLink,
} from 'lucide-react'
import { RichTextEditor } from '@/components/custom-pages/RichTextEditor'

type CustomPage = Tables<'custom_pages'>

// Form schema
const pageSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title is too long'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(100, 'Slug is too long')
    .regex(
      /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/,
      'Slug must contain only lowercase letters, numbers, and hyphens'
    ),
  meta_description: z.string().max(160, 'Meta description is too long').optional().nullable(),
  is_published: z.boolean().default(false),
  show_in_navigation: z.boolean().default(false),
  navigation_order: z.number().min(0).default(0),
})

type PageFormData = z.infer<typeof pageSchema>

export function EditCustomPagePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Content state (separate from form to handle TipTap)
  const [content, setContent] = useState('')
  const [contentLoaded, setContentLoaded] = useState(false)

  const activeAccount = isImpersonating ? impersonatedAccount : account

  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Fetch page data
  const { data: page, isLoading } = useQuery({
    queryKey: ['custom-page', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('custom_pages')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      return data as CustomPage
    },
    enabled: !!id,
  })

  const form = useForm<PageFormData>({
    resolver: zodResolver(pageSchema),
    defaultValues: {
      title: '',
      slug: '',
      meta_description: '',
      is_published: false,
      show_in_navigation: false,
      navigation_order: 0,
    },
  })

  // Update form when page loads
  useEffect(() => {
    if (page) {
      form.reset({
        title: page.title,
        slug: page.slug,
        meta_description: page.meta_description || '',
        is_published: page.is_published ?? false,
        show_in_navigation: page.show_in_navigation ?? false,
        navigation_order: page.navigation_order ?? 0,
      })
      setContent(page.content)
      setContentLoaded(true)
    }
  }, [page, form])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: PageFormData) => {
      if (!id) throw new Error('Page ID is required')

      const { error } = await supabase
        .from('custom_pages')
        .update({
          title: data.title,
          slug: data.slug,
          content,
          meta_description: data.meta_description || null,
          is_published: data.is_published,
          show_in_navigation: data.show_in_navigation,
          navigation_order: data.navigation_order,
        })
        .eq('id', id)

      if (error) {
        if (error.code === '23505') {
          throw new Error('A page with this slug already exists')
        }
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-pages'] })
      queryClient.invalidateQueries({ queryKey: ['custom-page', id] })
      toast({
        title: 'Page saved',
        description: 'Your changes have been saved.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error saving page',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Toggle publish
  const togglePublishMutation = useMutation({
    mutationFn: async (publish: boolean) => {
      if (!id) throw new Error('Page ID is required')

      const { error } = await supabase
        .from('custom_pages')
        .update({ is_published: publish })
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: (_, publish) => {
      queryClient.invalidateQueries({ queryKey: ['custom-page', id] })
      toast({
        title: publish ? 'Page published' : 'Page unpublished',
        description: publish
          ? 'The page is now visible on your website.'
          : 'The page is no longer visible on your website.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: PageFormData) => {
    saveMutation.mutate(data)
  }

  const getPreviewUrl = () => {
    if (!page?.slug || !activeAccount?.slug) return '#'
    const viewerBase =
      import.meta.env.VITE_VIEWER_URL || 'http://localhost:3001'
    return `${viewerBase.replace('localhost', `${activeAccount.slug}.localhost`)}/p/${page.slug}`
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-[600px] w-full" />
      </div>
    )
  }

  if (!page) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <h2 className="text-xl font-semibold">Page not found</h2>
        <p className="text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
        <Button
          variant="link"
          onClick={() => navigate(`${basePath}/pages`)}
          className="mt-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Pages
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`${basePath}/pages`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{page.title}</h1>
            <p className="text-muted-foreground">/p/{page.slug}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {page.is_published ? (
            <>
              <Button variant="outline" size="sm" asChild>
                <a href={getPreviewUrl()} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Page
                </a>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => togglePublishMutation.mutate(false)}
                disabled={togglePublishMutation.isPending}
              >
                <EyeOff className="mr-2 h-4 w-4" />
                Unpublish
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => togglePublishMutation.mutate(true)}
              disabled={togglePublishMutation.isPending}
            >
              <Eye className="mr-2 h-4 w-4" />
              Publish
            </Button>
          )}
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save
          </Button>
        </div>
      </div>

      <Tabs defaultValue="content" className="space-y-4">
        <TabsList>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="settings">Page Settings</TabsTrigger>
        </TabsList>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {/* Content Tab */}
            <TabsContent value="content" className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Page Title</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Privacy Policy" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {contentLoaded && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Page Content</label>
                  <RichTextEditor
                    content={content}
                    onChange={setContent}
                    placeholder="Start writing your page content..."
                  />
                </div>
              )}
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>URL & SEO</CardTitle>
                  <CardDescription>
                    Configure the page URL and search engine optimization
                    settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>URL Slug</FormLabel>
                        <FormControl>
                          <div className="flex items-center">
                            <span className="text-sm text-muted-foreground mr-1">
                              /p/
                            </span>
                            <Input placeholder="privacy-policy" {...field} />
                          </div>
                        </FormControl>
                        <FormDescription>
                          The URL path for this page
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="meta_description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="A brief description of this page for search engines..."
                            className="resize-none"
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormDescription>
                          A brief description for search engines (max 160
                          characters)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Visibility & Navigation</CardTitle>
                  <CardDescription>
                    Control how this page appears on your website
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="is_published"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Published</FormLabel>
                          <FormDescription>
                            Make this page visible on your public website
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="show_in_navigation"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Show in Navigation</FormLabel>
                          <FormDescription>
                            Add this page to your website's navigation menu
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {form.watch('show_in_navigation') && (
                    <FormField
                      control={form.control}
                      name="navigation_order"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Navigation Order</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              {...field}
                              onChange={(e) =>
                                field.onChange(parseInt(e.target.value) || 0)
                              }
                            />
                          </FormControl>
                          <FormDescription>
                            Lower numbers appear first in the navigation
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </form>
        </Form>
      </Tabs>
    </div>
  )
}
